import { useMemo } from 'react'

import omit from 'lodash.omit'
import {
  GetInvoicesForSettlement,
  GetInvoicesForSettlementVariables,
  GetReceiptsForSettlement,
  GetReceiptsForSettlementVariables,
  GetSettlementByUuidQuery,
  GetSettlementByUuidQueryVariables,
  GetSettlementQuery,
  GetSettlementQueryVariables,
  GenerateSettlementReportMutation,
  GenerateSettlementReportMutationVariables,
  ShareSettlementReportMutation,
  ShareSettlementReportMutationVariables,
} from 'types/graphql'

import { QueryKey, useMutation, useQuery } from 'src/utils/queryClient'

const GetSettlementQueryDocument = gql`
  query GetSettlementQuery($id: Int!) {
    getSettlement(id: $id) {
      ...SettlementFragment
    }
  }
`

export const useGetSettlementQuery = (id: number | null | undefined) => {
  const enabled = typeof id === 'number'
  const res = useQuery<GetSettlementQuery, GetSettlementQueryVariables>({
    queryKey: [QueryKey.GetSettlement, id],
    queryDocument: GetSettlementQueryDocument,
    variables: { id: id ?? 0 },
    enabled,
  })

  const settlement = useMemo(() => res.data?.getSettlement, [res.data?.getSettlement])

  return {
    ...omit(res, 'data'),
    settlement,
  }
}

const GetSettlementByUuidQueryDocument = gql`
  query GetSettlementByUuidQuery($uuid: String!) {
    getSettlementByUuid(uuid: $uuid) {
      ...SettlementFragment
    }
  }
`

export const useGetSettlementByUuidQuery = (uuid: string | null | undefined) => {
  const enabled = typeof uuid === 'string' && uuid.length > 0
  const res = useQuery<GetSettlementByUuidQuery, GetSettlementByUuidQueryVariables>({
    queryKey: [QueryKey.GetSettlementByUuid, uuid],
    queryDocument: GetSettlementByUuidQueryDocument,
    variables: { uuid: uuid || '' },
    enabled,
  })

  const settlement = useMemo(() => res.data?.getSettlementByUuid, [res.data?.getSettlementByUuid])

  return {
    ...omit(res, 'data'),
    settlement,
  }
}

const GetInvoices = gql`
  query GetInvoicesForSettlement($settlementId: Int!) {
    getInvoicesForSettlement(settlementId: $settlementId) {
      ...FileFragment
    }
  }
`

export const useGetInvoicesForSettlementQuery = (settlementId: number | null | undefined) => {
  const res = useQuery<GetInvoicesForSettlement, GetInvoicesForSettlementVariables>({
    queryKey: [QueryKey.GetInvoicesForSettlement, settlementId],
    queryDocument: GetInvoices,
    variables: { settlementId: settlementId ?? 0 },
    enabled: false,
  })
  const invoices = useMemo(
    () => res.data?.getInvoicesForSettlement,
    [res.data?.getInvoicesForSettlement]
  )
  return {
    ...omit(res, 'data'),
    invoices,
  }
}

const GetReceipts = gql`
  query GetReceiptsForSettlement($settlementId: Int!) {
    getReceiptsForSettlement(settlementId: $settlementId) {
      ...FileFragment
    }
  }
`

export const useGetReceiptsForSettlementQuery = (settlementId: number | null | undefined) => {
  const res = useQuery<GetReceiptsForSettlement, GetReceiptsForSettlementVariables>({
    queryKey: [QueryKey.GetReceiptsForSettlement, settlementId],
    queryDocument: GetReceipts,
    variables: { settlementId: settlementId ?? 0 },
    enabled: false,
  })
  const receipts = useMemo(
    () => res.data?.getReceiptsForSettlement,
    [res.data?.getReceiptsForSettlement]
  )
  return {
    ...omit(res, 'data'),
    receipts,
  }
}

const GenerateSettlementReport = gql`
  mutation GenerateSettlementReportMutation($settlementId: Int!) {
    generateSettlementReport(settlementId: $settlementId) {
      ...FileFragment
    }
  }
`

export const useGenerateSettlementReportMutation = () => {
  return useMutation<GenerateSettlementReportMutation, GenerateSettlementReportMutationVariables>({
    mutationDocument: GenerateSettlementReport,
  })
}

const ShareSettlementReport = gql`
  mutation ShareSettlementReportMutation($input: ShareSettlementReportInput!) {
    shareSettlementReport(input: $input)
  }
`

export const useShareSettlementReportMutation = () => {
  return useMutation<ShareSettlementReportMutation, ShareSettlementReportMutationVariables>({
    mutationDocument: ShareSettlementReport,
  })
}
